import { Stack, Box, Typography, Button, Popover } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';



import MeetingDetails from './meetingsDetails';

import CalendarIcon from '../../../assets/svg/calendarPlus.svg';
import { MEETING_RESPONSE, WISTLEBLOWER } from '../../../constants/meetings';
import { MeetingDetailsObjectFromAPI } from '../../../interfaces/meetingDetailsObjectFromAPI';
import LoadingComponent from '../../common/loadingComponent';

export interface MeetingDetailsObject {
  id: number;
  organizer: string;
  date: string;
  dateCreated: string;
  type: number;
}

const styles = {
  bodyTextStyle: {
    fontWeight: 500,
    fontSize: '14px',
  },
  buttonStyle: {
    textTransform: 'none',
    bgcolor: 'transparent',
    borderRadius: '6px',
    color: '#6D6D6D',
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '14px',
    '&:hover': {
      color: '#000000',
    },
  },
};

// FILTER THE DATA FROM API TO LIST ONLY THE ACCEPTED MEETINGS BY THE WB USER
const filterDataFromAPI = (data: MeetingDetailsObjectFromAPI[]): MeetingDetailsObjectFromAPI[] => {
  return data?.filter(
    meeting => {
      let accepted = false;
      if (meeting?.attendees) {
        accepted = meeting.attendees.some(({ attendee, confirm }) => {
          return confirm === MEETING_RESPONSE.PENDING && attendee?.user_type === WISTLEBLOWER;
        });
      }
      return accepted;
    });
};

const transformDataFromAPI = (data: MeetingDetailsObjectFromAPI[]): MeetingDetailsObject[] => {
  if (!Array.isArray(data)) return [];
  return filterDataFromAPI(data)?.map(({ id, date_and_time, created_by, type, created_at }: MeetingDetailsObjectFromAPI) => (
    {
      id, 
      type,
      date: date_and_time,
      organizer: created_by?.full_name ?? '--',
      dateCreated: created_at,
    }
  ));
};

const CollapsibleSchedule = () => {
  const [meetingList, setMeetingList] = useState<MeetingDetailsObject[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const anchorEl = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number>(0);
  const { reportDetailsId } = useParams();

  const openPopoverHandler = () => {
    setOpen(true);
    setWidth(anchorEl?.current?.offsetWidth ?? 0);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const { data: meetingRes, isLoading, mutate } = useSWR(reportDetailsId ? `schedule/meetings?report=${reportDetailsId}` : '', { revalidateOnFocus: false });

  useEffect(() => {
    setMeetingList(transformDataFromAPI(meetingRes?.results));
  }, [meetingRes]);


  return (
    <Stack>
      <Stack ref={anchorEl} direction='row' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' gap={1}>
          {!open &&
            <>
              <Box component='img' src={CalendarIcon} sx={{ width: '20px' }}/>
              <Typography sx={styles.bodyTextStyle}>
                <FormattedMessage defaultMessage='Scheduled meet invitation' /> 
              </Typography>
              {meetingList?.length > 0 &&
                <Box sx={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50px',
                  bgcolor: '#F83B3B',
                }}/>
              }
            </>
          }
        </Stack>
        <Button 
          sx={styles.buttonStyle}
          onClick={openPopoverHandler}
        >
          <FormattedMessage defaultMessage='Show' /> 
        </Button>
      </Stack>
      <Popover 
        id='meeting-popover'
        open={open && width !== 0}
        onClose={handleClose}
        anchorEl={anchorEl?.current}    
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderTop: '1px solid #D6D6D6',
            boxShadow: 'none!important',
            borderRadius: '0px',
          },
          
        }}
      >
        <Stack gap={2} sx={{
          width: (width + 16),
          padding: '12px',
          maxHeight: '180px',
          position: 'relative',
          paddingBottom: '10px',
        }}>
          <Stack direction='row' justifyContent='flex-end' sx={{
            position: 'absolute',
            top: '6px',
            right: '6px',
          }}>
            <Button 
              sx={styles.buttonStyle}
              onClick={handleClose}
            >
              <FormattedMessage defaultMessage='Hide' /> 
            </Button>
          </Stack>
          {isLoading && <LoadingComponent size={20}/>}
          {!isLoading && meetingList &&
          meetingList?.map((data) => (
            <MeetingDetails key={data.id} refresh={mutate} {...data}/>
          ))
          }
          {!isLoading && meetingList?.length === 0 &&
            <Typography>
              <FormattedMessage defaultMessage='No meetings scheduled' />
            </Typography>
          }
        </Stack>
      </Popover>
    </Stack>
  );
};

export default CollapsibleSchedule;